import { MemberProfileEmptyPage } from "@components/common/empty-page/member-profile-empty-page";
import { MemberGroupsTable } from "@components/common/member/member-groups-table";
import { MemberProfileSkeleton } from "@components/common/skeleton-loading/member-profile-skeleton";
import { MemberProfileInfoBar } from "@components/member-profile/member-profile-info-bar";
import { UserInfo } from "@components/user-info/user-info";
import { isCompanyMemberDetails } from "@custom-types/member-type-guards";
import { BaseMemberProps } from "@custom-types/member-types";
import { PartialNull } from "@custom-types/types";
import { useDynamicHeight } from "@hooks/use-dynamic-height";
import { useMediaQueryList } from "@hooks/use-media-query";
import { useMemberGroups } from "@hooks/use-member-groups";
import { Box, Grid2, Stack } from "@mui/material";
import { TeamDetailsInfoBar } from "@pages/members/teams/team-details/team-details-info-bar";
import { NotFoundPage } from "@pages/not-found-page";
import { FaroCoreApiTypes } from "@stellar/core-api-client";

interface Props extends PartialNull<BaseMemberProps> {
  /** Flag to indicate if the content of the member is loading */
  isLoading: boolean;

  /** This is defined if the overview page is displaying a team instead of an individual member */
  team?: FaroCoreApiTypes.Team;
}

/**
 * Page to show the overview of the profile of the selected user.
 */
export function MemberOverviewPage({
  member,
  team,
  isLoading,
}: Props): JSX.Element {
  const { groups, companyMemberGroupsStatus } = useMemberGroups();
  const { isScreenMdOrSmaller } = useMediaQueryList();
  const { ref: groupsContainerRef, height: dynamicHeight } = useDynamicHeight({
    shouldSkip: isScreenMdOrSmaller,
  });

  const isFetching = isLoading || companyMemberGroupsStatus === "pending";

  if (isFetching || companyMemberGroupsStatus === "uninitialized") {
    return <MemberProfileSkeleton />;
  }

  // Show not found page if fetching member or groups failed
  // or if the member entity is not of type CompanyMemberDetails
  if (
    !member ||
    !isCompanyMemberDetails(member) ||
    companyMemberGroupsStatus !== "succeeded"
  ) {
    return <NotFoundPage />;
  }

  return (
    <Grid2
      data-testid={
        member.kind !== "user-group"
          ? "MEMBER_PROFILE_OVERVIEW_CONTAINER"
          : "TEAM_PROFILE_OVERVIEW_CONTAINER"
      }
    >
      {
        member.kind === "user-group" ? (
          <TeamDetailsInfoBar />
        ) : (
          <MemberProfileInfoBar member={member} />
        )
      }

      {/* Container for the projects, groups and user info component */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column-reverse", lg: "row" },
          width: "100%",
          gap: "20px",
        }}
      >
        {/* Container for the projects and groups */}
        <Box
          ref={groupsContainerRef}
          component="div"
          marginTop={"14px"}
          sx={{
            flex: {
              lg: "1 1 0",
              xs: "unset",
            },
            minWidth: 0,
            maxHeight: {
              lg: dynamicHeight,
              xs: "100%",
            },
            overflowY: "auto",
          }}
        >
          {
            groups.length === 0 ? (
              <MemberProfileEmptyPage member={member} />
            ) : (
              <MemberGroupsTable
                groups={groups}
                member={member}
              />
            )
          }
        </Box>

        {/* Container for the user info */}
        {(
          <Stack marginTop="14px" alignItems="center">
            <UserInfo member={member} team={team} />
          </Stack>
        )}
      </Box>
    </Grid2>
  );
}
